import { apiClient } from '@/services/api';
import { logger } from '@/utils/logger/loggerInstance';

// Messages "god mode": Admin/SuperAdmin (the supervising brokers) oversee every advisor–client thread on the platform.
// These shapes are the contract with packages/functions/api/admin/conversations/*.

export type ConversationSenderRole = 'advisor' | 'member' | 'support';

export interface AdminConversationPerson {
  userId?: string;
  name: string;
  email?: string;
  role: string;
  photoUrl?: string;
}

export interface AdminConversation {
  groupId: string;
  holdingName: string;
  status: 'active' | 'archived';
  advisor: { userId: string; name: string; photoUrl?: string };
  coAdvisors: { userId: string; name: string }[];
  participants: AdminConversationPerson[];
  lastMessage?: {
    content: string;
    senderName: string;
    senderRole: ConversationSenderRole;
    createdAt: number;
    deleted?: boolean;
  };
  messageCount: number;
  lastActivityAt?: number;
  // the last message came from a client and no advisor or support reply has followed it
  awaitingReply: boolean;
  awaitingSince?: number;
}

export interface AdminConversationsQuery {
  limit?: number;
  cursor?: string;
  advisorId?: string;
  status?: 'active' | 'archived';
  awaiting?: boolean;
  q?: string;
}

export interface AdminConversationsResponse {
  conversations: AdminConversation[];
  nextCursor?: string;
}

export interface ConversationReadState {
  participants: { userId: string; name: string; role: string; lastReadAt?: number }[];
}

export interface ConversationView {
  adminId: string;
  adminName: string;
  viewedAt: number;
}

export const listAdminConversations = async (
  query: AdminConversationsQuery = {},
): Promise<AdminConversationsResponse> => {
  logger.debug('GET /admin/conversations', query);
  const response = await apiClient.get<AdminConversationsResponse>('/admin/conversations', {
    params: { ...query, awaiting: query.awaiting ? 'true' : undefined },
  });
  return response.data;
};

export const getConversationReadState = async (groupId: string): Promise<ConversationReadState> => {
  const response = await apiClient.get<ConversationReadState>(`/admin/conversations/${groupId}/read-state`);
  return response.data;
};

export const listConversationViews = async (groupId: string): Promise<{ views: ConversationView[] }> => {
  const response = await apiClient.get<{ views: ConversationView[] }>(`/admin/conversations/${groupId}/views`);
  return response.data;
};

// records that the signed-in admin opened this thread in god mode (the broker's oversight trail)
export const recordConversationView = async (groupId: string): Promise<void> => {
  await apiClient.post(`/admin/conversations/${groupId}/views`);
};
